import {
  MotifButton,
  MotifErrorMessage,
  MotifLabel,
  MotifModal,
  MotifModalBody,
  MotifModalHeader,
  MotifDatePicker,
  MotifFormField,
  MotifToast,
  MotifIcon,
  MotifTooltip,
} from '@ey-xd/motif-react'
import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AsyncSelect from 'react-select/async'
import CreatableSelect from 'react-select/creatable'
import { openClientCard } from './clientCardSlice'

import type {
  IUserSearchOption,
  ClientSearchOption,
} from '@/common/models/User'
import { TeamMemberType, type EngagementCardData } from '../../services/types'
import {
  useSaveClientMutation,
  useLazyListClientQuery,
  useLazyUsersListQuery,
  useSaveNewClientMutation,
} from './clientApi'
import { addClient, closeClientCard, updateClient } from './clientCardSlice'
import { getSelectOptions, getSelectUserOptions } from '../../utils/common'
import { ENGAGEMENT_ID } from './Constants'
import styles from './index.module.scss'
import { LooseValuePiece } from '@ey-xd/motif-react/components/forms/date-picker/date-picker.interface'
import { useForm, Controller } from 'react-hook-form'
import ControlInputBox from '@/components/Form/InputBox/ControlInputBox'
import { UserAvatar } from '@/components/UserAvatar/UserAvatar'
import { useGetUserEmailQuery } from '@/services/graphApi'
import { IconoirInfoCircle } from '@ey-xd/motif-icon'

type SelectValue = {
  label: string
  value: string
  profileImage?: string | null
}

// Selected chip only - shows the MotifTooltip (portal-based, fine for the
// handful of static chips).
const UserOptionLabel: React.FC<{ option: SelectValue }> = ({ option }) => {
  const { data: email } = useGetUserEmailQuery(option.value)

  return (
    <MotifTooltip
      hideCloseButton
      allowHover
      trigger={
        <div className={styles.userOption}>
          <UserAvatar
            adIdentifier={option.value}
            label={option.label}
            profileImage={option.profileImage}
            className={styles.userOptionAvatar}
          />
          <span>{option.label}</span>
        </div>
      }
    >
      <div className="motif-caption-default-light">{email ?? option.label}</div>
    </MotifTooltip>
  )
}

// Dropdown menu row - a plain title attribute instead of MotifTooltip. With
// many rows open at once, every row's floating-tooltip portal repositioning
// as its avatar image loaded was tripping react-select's outside-click
// detection and closing the menu.
const UserOptionRow: React.FC<{ option: SelectValue }> = ({ option }) => {
  const { data: email } = useGetUserEmailQuery(option.value)

  return (
    <div className={styles.userOption} title={email ?? option.label}>
      <UserAvatar
        adIdentifier={option.value}
        label={option.label}
        profileImage={option.profileImage}
        className={styles.userOptionAvatar}
      />
      <span>{option.label}</span>
    </div>
  )
}
type ClientEngagementModalProps = {
  refreshEngagements: () => void  
}
//TODO: Separate action functions from the form component
export const ClientEngagementModal: React.FC<
  ClientEngagementModalProps
> = ({
  refreshEngagements
}) => {
  const calendarFormat = 'dd-MM-yyyy'
  const [clientCardData, setClientCardData] = useState<any | null>({
    id: ENGAGEMENT_ID,
    clientId: '',
    clientName: '',
    engagementName: '',
    engagementCode: '',
    signOffDueDate: null,
    team: [],
  })
  const [isLoading, setIsLoading] = useState(false)
  const [clientName, setClientName] = useState<SelectValue | null>({
    label: '',
    value: '',
  })

  const [engagementUsers, setEngagementUsers] = useState<SelectValue[]>([])
  const [clientNameList, setClientNameList] = useState<ClientSearchOption[]>([])
  const [uploadStatus, setUploadStatus] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [saveClientCard, { data: client }] = useSaveClientMutation()
  const [listClient] = useLazyListClientQuery()
  const [usersList] = useLazyUsersListQuery()
  const [saveNewClient] = useSaveNewClientMutation()
  const validationMessageRef = useRef()
  const dispatch = useDispatch()

  const cloneClientCardData = (
    id: string | null,
    engagementName: string,
    client: SelectValue,
    engagementCode: string,
    engagementUsers: SelectValue[],
    signOffDueDate?: Date | null,
  ) => {
    const clientCardData: EngagementCardData = {
      id: id,
      engagementName: engagementName,
      engagementCode: engagementCode,
      clientId: client.value,
      clientName: client.label,
      signOffDueDate: signOffDueDate ? signOffDueDate.toISOString() : null,
      team: [],
    }
    engagementUsers
      .map((user) => ({
        value: user.value,
        label: user.label,
        userName: user.label,
        userId: user.value,
        memberType: TeamMemberType.Member,
      }))
      .forEach((user: IUserSearchOption) => {
        clientCardData.team.push({
          userId: user.value ?? '',
          userName: user.label ?? '',
          memberType: TeamMemberType.Member,
        })
      })

    return clientCardData
  }

  const isOpen = useSelector(
    (state: { clientCard: { isOpen: boolean } }) => state.clientCard.isOpen,
  )
  const editEngagementIndex = useSelector(
    (state: { clientCard: { editEngagementCardIndex: number | null } }) =>
      state.clientCard.editEngagementCardIndex,
  )
  const clientList = useSelector(
    (state: { clientCard: { clients: EngagementCardData[] | undefined } }) =>
      state.clientCard.clients || [],
  )

  useEffect(() => {
    if (isOpen) {
      listClient().then((response) => {
        if (response.data) {
          setClientNameList(getSelectOptions(response.data))
        }
      })
      setClientCardData({
        id: ENGAGEMENT_ID,
        clientId: '',
        clientName: null,
        engagementName: null,
        engagementCode: '',
        signOffDueDate: null,
        team: [],
      })
    }
  }, [isOpen])

  useEffect(() => {
    if (editEngagementIndex !== null) {
      listClient().then((response) => {
        if (response.data) {
          setClientNameList(getSelectOptions(response.data))
        }
      })

      const client =
        editEngagementIndex !== null ? clientList[editEngagementIndex] : null
      if (client) {
        setValue('engagementName', client.engagementName)
        setValue('clientEngagementId', client.engagementCode)
        setValue('clientName', {
          label: client.clientName,
          value: client.clientId,
        })
        setValue(
          'signOffDueDate',
          client.signOffDueDate ? new Date(client.signOffDueDate) : null,
        )
        setValue('engagementUsers', getSelectUserOptions(client.team))
        setClientCardData({
          id: client.id,
          clientName: { label: client.clientName, value: client.clientId },
          engagementName: client.engagementName,
          engagementCode: client.engagementCode,
          signOffDueDate: client.signOffDueDate,
          team: getSelectUserOptions(client.team),
          isArchived: client.isArchived,
        })
      }
    }
  }, [editEngagementIndex])

  // Timer lives in a ref so paste (single value change) and typed keystrokes
  // debounce the same way, instead of a property on the recreated callback.
  const getUserListTimeout = useRef<ReturnType<typeof setTimeout>>()
  // Track the in-flight request so a slow, now-stale search can't queue
  // behind the browser's per-host connection limit and delay the real answer.
  const getUserListRequest = useRef<ReturnType<typeof usersList>>()

  useEffect(() => {
    return () => {
      clearTimeout(getUserListTimeout.current)
      getUserListRequest.current?.abort()
    }
  }, [])

  const getUserList: any = (value: string, callback: any) => {
    clearTimeout(getUserListTimeout.current)
    getUserListRequest.current?.abort()

    // Pasted names can carry leading/trailing whitespace or non-breaking
    // spaces (e.g. copied from Outlook/Excel) - normalize before checking length.
    const searchTerm = value.replace(/\u00a0/g, ' ').trim()

    if (searchTerm.length <= 2) {
      callback([])
      return
    }

    getUserListTimeout.current = setTimeout(() => {
      const request = usersList(searchTerm)
      getUserListRequest.current = request

      request
        .unwrap()
        .then((data) => {
          // A newer search superseded this one - its own .then will supply the result.
          if (getUserListRequest.current !== request) return

          const options: SelectValue[] = []
          data?.forEach((user) => {
            options.push({
              label: user.displayName,
              value: user.adIdentifier,
              profileImage: user.profileImage,
            })
          })
          callback(options)
        })
        .catch((err) => {
          // Aborted (superseded) requests aren't real failures - only surface genuine errors.
          if (err?.name === 'AbortError' || getUserListRequest.current !== request) return
          callback([])
        })
    }, 300) // debounce only delays firing the request, not rendering the result
  }

  useEffect(() => {
    if (uploadStatus !== null) {
      const timer = setTimeout(() => {
        setUploadStatus(null)
        setErrorMessage('')
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [uploadStatus])

  const formatCreateLabel = (inputValue: string) => {
    return (
      <div className={styles.formatCreateLabel}>
        {' '}
        <span>{inputValue}</span>{' '}
        <MotifButton variant="text-alt" size="small" type="button">
          {' '}
          + Add
        </MotifButton>
      </div>
    )
  }

  const isValidClientName = (inputValue: string, value: any, options: any) => {
    const result = options.filter((item: any) =>
      item.label.toLowerCase().includes(inputValue.toLowerCase()),
    )
    return result.length > 0 ? false : true
  }

  const handleCreateNewClient = (inputValue: string) => {
    const payload = {
      id: ENGAGEMENT_ID,
      clientName: inputValue,
      engagements: [],
    }
    saveNewClient(payload)
      .unwrap()
      .then((client: any) => {
        setClientName({ label: client.clientName, value: client.id })
        setValue('clientName', {
          label: client.clientName,
          value: client.id,
        })
        setClientNameList((prevList) => [
          ...prevList,
          {
            id: client.id,
            name: client.clientName,
            label: client.clientName,
            value: client.id,
          },
        ])
      })
  }

  const closeMessageHandler = () => {
    setUploadStatus(null)
    setErrorMessage('')
  }

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    setValue,
    reset,
  } = useForm<any>({
    defaultValues: {
      clientName: '',
      engagementName: '',
      clientEngagementId: '',
      signOffDueDate: null,
      engagementUsers: '',
    },
  })
  const closeForm = () => {
    setClientCardData({
      id: ENGAGEMENT_ID,
      clientId: '',
      clientName: null,
      engagementName: null,
      engagementCode: '',
      signOffDueDate: null,
      team: [],
    })
    reset()
    setUploadStatus(null)
    setValue('engagementName', '')
    setValue('clientEngagementId', '')
    setValue('clientName', null)
    setValue('signOffDueDate', null)
    setValue('engagementUsers', null)
    dispatch(closeClientCard())
  }

  const onSubmit = (data: any) => {
    console.log('Form submitted:', data)
    const {
      clientName,
      clientEngagementId,
      engagementName,
      engagementUsers,
      signOffDueDate,
    } = data

    const clientData = cloneClientCardData(
      clientCardData.id,
      engagementName,
      clientName,
      clientEngagementId,
      engagementUsers,
      signOffDueDate,
    )

    const handleSaveClient = async (
      clientData: EngagementCardData,
      isEdit: boolean,
    ) => {
      setIsLoading(true)
      try {
        const client = await saveClientCard(clientData).unwrap()
        if (isEdit && editEngagementIndex !== null) {
          dispatch(
            updateClient({
              ...clientData,
              team: client.team,
              index: editEngagementIndex,
              signOffDueDate: clientData.signOffDueDate ?? null,
            }),
          )
        } else {
          dispatch(addClient(client))
        }
        dispatch(openClientCard(null))
        closeForm()
      } catch (error: any) {
        setUploadStatus('error')
        setErrorMessage(
          error.data?.message || error.data || 'An error occurred',
        )
        validationMessageRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
        console.error('Failed to save client card:', error)
      } finally {
        setIsLoading(false)
        refreshEngagements()
      }
    }

    handleSaveClient(clientData, editEngagementIndex !== null)
  }

  return (
    <MotifModal show={isOpen} onClose={() => closeForm()} size="md">
      <MotifModalHeader
        className={styles.MotifModalHeader}
        closeButtonProps={{
          'aria-label': 'Custom Close Button aria-label',
          title: 'Custom Close Button title',
        }}
      >
        {editEngagementIndex != null
          ? 'Edit client engagement card'
          : 'Create new client engagement card'}
      </MotifModalHeader>
      <MotifModalBody
        style={{
          paddingBottom: '',
        }}
      >
        <div ref={validationMessageRef}>
          {uploadStatus !== null && (
            <MotifToast onClose={closeMessageHandler} variant="error">
              {errorMessage}
            </MotifToast>
          )}
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ControlInputBox
            control={control}
            name="engagementName"
            label="Engagement name"
            required={true}
            isTextArea={false}
            showLabel={true}
            readonly={false}
            placeholder="EX : TD Bank Main SOX Audit; CI Financial FAIT Audit; Sunlife SOC 1 Type 2 Report"
            aria-describedby={
              errors.engagementName ? 'error-message-name' : null
            }
            aria-invalid={errors.engagementName ? true : false}
          />
          {errors.engagementName && (
            <MotifErrorMessage id="error-message-name">
              Please provide an engagement name!
            </MotifErrorMessage>
          )}
          <ControlInputBox
            control={control}
            name="clientEngagementId"
            label="Client engagement Id"
            required={true}
            isTextArea={false}
            showLabel={true}
            readonly={false}
            placeholder="EX : E-678XXX"
            aria-describedby={
              errors.clientEngagementId ? 'error-message-name' : null
            }
            aria-invalid={errors.clientEngagementId ? true : false}
          />
          {errors.clientEngagementId && (
            <MotifErrorMessage id="error-message-name">
              Please provide a client engagement Id!
            </MotifErrorMessage>
          )}
          <MotifFormField>
            <MotifLabel className={styles.MotifFormField}>
              Client name
              <span className="required">*</span>
            </MotifLabel>
            <Controller
              name="clientName"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value, ...args } }) => {
                return (
                  <CreatableSelect
                    {...args}
                    className={`${errors.clientName ? 'error' : ''}`}
                    classNamePrefix="react-select"
                    id="select-client"
                    isClearable
                    placeholder="Enter name"
                    value={value}
                    onChange={(selectedOption) => {
                      setClientName(selectedOption)
                      onChange(selectedOption)
                    }}
                    options={clientNameList}
                    createOptionPosition={'last'}
                    isValidNewOption={isValidClientName}
                    formatCreateLabel={formatCreateLabel}
                    onCreateOption={handleCreateNewClient}
                  />
                )
              }}
            />
            {errors.clientName && (
              <MotifErrorMessage id="error-message-name">
                Please provide a client name!
              </MotifErrorMessage>
            )}
          </MotifFormField>
          <MotifFormField
            className={styles.MotifFormField}
            role="group"
            aria-labelledby="clientEngagementIdLabel"
          >
            <MotifLabel htmlFor="signOffDueDate">
              Audit Report Date
              <span className="required">*</span>
            </MotifLabel>
            <Controller
              name="signOffDueDate"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value, ...args } }) => {
                return (
                  <MotifDatePicker
                    {...args}
                    id="signOffDueDate"
                    value={value}
                    format={calendarFormat}
                    onChange={(date: Date | Range<LooseValuePiece>) => {
                      if (date instanceof Date) {
                        onChange(date)
                      } else {
                        console.error('Unexpected date format:', date)
                      }
                    }}
                    clearButtonTitle="Clear Audit Report Date"
                    className={styles.datePickerInput}
                    dayPlaceholder="DD"
                    maxDate={new Date(NaN)}
                    minDate={value ? undefined : new Date()}
                    monthPlaceholder="MM"
                    yearPlaceholder="YYYY"
                    disabled={clientCardData.isArchived}
                  />
                )
              }}
            />
            <span className="motif-caption-default-light">
              The date should be in accordance with the engagement deadline.
            </span>
            {errors.signOffDueDate && (
              <MotifErrorMessage id="error-message-name">
                Please provide a Audit Report Date!
              </MotifErrorMessage>
            )}
          </MotifFormField>
          <MotifFormField
            className={styles.MotifFormField}
            role="group"
            aria-labelledby="teamMembersLabel"
          >
            <MotifLabel htmlFor="engagementUsers">
              Team members assignment<span className="required">*</span>              
            </MotifLabel>
            <Controller
              name="engagementUsers"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value, ...args } }) => {
                return (
                  <AsyncSelect
                    {...args}
                    id="engagementUsers"
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                    }}
                    className={`${errors.engagementUsers ? 'error' : ''}`}
                    classNamePrefix="react-select"
                    aria-label="Search"
                    placeholder="Search by name"
                    onChange={(selectedOption) => {
                      setEngagementUsers(selectedOption as SelectValue[])
                      onChange(selectedOption)
                    }}
                    getOptionLabel={(e) => e.label}
                    getOptionValue={(e) => e.value}
                    formatOptionLabel={(e: SelectValue, { context }) =>
                      context === 'value' ? (
                        <UserOptionLabel option={e} />
                      ) : (
                        <UserOptionRow option={e} />
                      )
                    }
                    isSearchable={true}
                    isMulti
                    menuPosition="fixed"
                    loadOptions={getUserList}
                    noOptionsMessage={() => 'No users found'}
                    value={value}
                  />
                )
              }}
            />
            <span className="motif-caption-default-light">
              At least two users need to be added here.
              <MotifTooltip
                allowHover
                hideCloseButton
                flip
                forceHideCounter={0}
                id="tooltip"
                offset={[5, 8]}
                placement="bottom"
                trigger={
                  <MotifIcon
                    iconFunction={IconoirInfoCircle}
                    title="Team member assignment"
                    className="margin-left-5"
                  />
                }
                usePortal
                variant="default"
              >
                <div className="motif-body2-default-bold">Team member assignment</div>
                <div className="motif-caption-default-light motif-tooltip-custom-width">
                  When assigning a team member, this helps restrict access from viewing other engagements. Please ensure you reach out to your partner in charge to obtain confirmation to proceed with using this tool. 
                </div>
              </MotifTooltip>              
            </span>
            {errors.engagementUsers && (
              <MotifErrorMessage id="error-message-name">
                Please provide a team member assignment!
              </MotifErrorMessage>
            )}
          </MotifFormField>
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '1rem',
              }}
            >
              <MotifButton
                onClick={() => closeForm()}
                size="medium"
                type="button"
                variant="secondary"
                disabled={isLoading}
              >
                Cancel
              </MotifButton>
              <MotifButton
                className=""
                size="medium"
                type="submit"
                disabled={isLoading}
              >
                Save
              </MotifButton>
            </div>
          </div>
        </form>
      </MotifModalBody>
    </MotifModal>
  )
}
